import { API_BASE, adminAuthHeaders, apiRequest } from "./client";
import type {
  Category,
  DailyRegister,
  Dashboard,
  EmployeeShiftList,
  Holiday,
  HolidayScope,
  MantraDay,
  MantraMonthlySummary,
  MonthlyRegister,
  Notice,
  OverrideStatus,
  Person,
  PersonInput,
  RegisterFilter,
  SessionKey,
} from "../types";

const PREFIX = "/biometric";

export const adminApi = {
  login(password: string) {
    return apiRequest<{ ok: boolean }>(`${PREFIX}/admin/login`, {
      method: "POST",
      body: { password },
    });
  },

  getRegister(adminPassword: string, date: string, filter: RegisterFilter) {
    return apiRequest<DailyRegister>(`${PREFIX}/attendance/daily`, {
      adminPassword,
      params: { date, filter },
    });
  },

  recomputeDate(adminPassword: string, date: string) {
    return apiRequest<{ ok: boolean }>(`${PREFIX}/attendance/daily/recompute`, {
      method: "POST",
      adminPassword,
      body: { date },
    });
  },

  exportRegisterCsv(adminPassword: string, date: string, filter: RegisterFilter) {
    const url = new URL(`${API_BASE}${PREFIX}/attendance/daily/export`);
    url.searchParams.set("date", date);
    url.searchParams.set("filter", filter);
    return fetch(url.toString(), {
      headers: adminAuthHeaders(adminPassword),
    });
  },

  getDashboard(adminPassword: string, date: string) {
    return apiRequest<Dashboard>(`${PREFIX}/attendance/daily`, {
      adminPassword,
      params: { date, view: "dashboard" },
    });
  },

  getMonthlyRegister(adminPassword: string, month: string) {
    return apiRequest<MonthlyRegister>(`${PREFIX}/attendance/monthly`, {
      adminPassword,
      params: { month },
    });
  },

  exportMonthlyCsv(adminPassword: string, month: string) {
    const url = new URL(`${API_BASE}${PREFIX}/attendance/monthly/export`);
    url.searchParams.set("month", month);
    return fetch(url.toString(), {
      headers: adminAuthHeaders(adminPassword),
    });
  },

  listPeople(adminPassword: string) {
    return apiRequest<Person[]>(`${PREFIX}/persons`, { adminPassword });
  },

  getPerson(adminPassword: string, id: string) {
    return apiRequest<Person>(`${PREFIX}/persons/${id}`, { adminPassword });
  },

  createPerson(adminPassword: string, data: PersonInput) {
    return apiRequest<Person>(`${PREFIX}/persons`, {
      method: "POST",
      adminPassword,
      body: data,
    });
  },

  updatePerson(adminPassword: string, id: string, data: PersonInput) {
    return apiRequest<Person>(`${PREFIX}/persons/${id}`, {
      method: "PUT",
      adminPassword,
      body: data,
    });
  },

  listNotices(
    adminPassword: string,
    opts?: { seen?: boolean; from?: string; to?: string }
  ) {
    return apiRequest<Notice[]>(`${PREFIX}/notices`, {
      adminPassword,
      params: {
        seen: opts?.seen !== undefined ? String(opts.seen) : undefined,
        from: opts?.from || undefined,
        to: opts?.to || undefined,
      },
    });
  },

  decideNotice(adminPassword: string, id: string, decision: "approve" | "reject") {
    return apiRequest<Notice>(`${PREFIX}/notices/${id}`, {
      method: "PATCH",
      adminPassword,
      body: { decision },
    });
  },

  listMantras(
    adminPassword: string,
    view: "submitted" | "waived" | "overdue" = "submitted",
    opts?: { from?: string; to?: string }
  ) {
    return apiRequest<MantraDay[]>(`${PREFIX}/mantras`, {
      adminPassword,
      params: {
        view,
        from: opts?.from || undefined,
        to: opts?.to || undefined,
      },
    });
  },

  getMantraMonthlySummary(adminPassword: string, month: string) {
    return apiRequest<MantraMonthlySummary>(`${PREFIX}/mantras/summary`, {
      adminPassword,
      params: { month },
    });
  },

  decideMantra(
    adminPassword: string,
    id: string,
    decision: "waive" | "mantras" | "fine_paid" | "reject",
    notes?: string
  ) {
    return apiRequest<MantraDay>(`${PREFIX}/mantras/${id}`, {
      method: "PATCH",
      adminPassword,
      body: { decision, notes },
    });
  },

  waiveMantraDate(
    adminPassword: string,
    personId: string,
    date: string,
    notes?: string,
    waiveKind: "mantras" | "fine_paid" = "mantras"
  ) {
    return apiRequest<MantraDay>(`${PREFIX}/mantras/waive`, {
      method: "POST",
      adminPassword,
      body: {
        person_id: Number(personId),
        date,
        notes,
        waive_kind: waiveKind,
      },
    });
  },

  markNoticeSeen(adminPassword: string, id: string) {
    return apiRequest<Notice>(`${PREFIX}/notices/${id}/seen`, {
      method: "PATCH",
      adminPassword,
    });
  },

  listEmployeeShifts(adminPassword: string, date: string) {
    return apiRequest<EmployeeShiftList>(`${PREFIX}/employee-shifts`, {
      adminPassword,
      params: { date },
    });
  },

  upsertEmployeeShift(
    adminPassword: string,
    personId: string,
    data: {
      date: string;
      blocks: { start_time: string; end_time: string; sort_order?: number }[];
    }
  ) {
    return apiRequest<unknown>(`${PREFIX}/employee-shifts/${personId}`, {
      method: "PUT",
      adminPassword,
      body: data,
    });
  },

  deleteEmployeeShift(adminPassword: string, personId: string, date: string) {
    return apiRequest<unknown>(`${PREFIX}/employee-shifts/${personId}`, {
      method: "DELETE",
      adminPassword,
      params: { date },
    });
  },

  overrideSession(
    adminPassword: string,
    data: {
      person_id: number;
      date: string;
      category: Category;
      session: SessionKey;
      status: OverrideStatus;
      late_minutes?: number;
      reason: string;
    }
  ) {
    return apiRequest<unknown>(`${PREFIX}/attendance/sessions/override`, {
      method: "POST",
      adminPassword,
      body: data,
    });
  },

  clearSessionOverride(
    adminPassword: string,
    data: {
      person_id: number;
      date: string;
      category: Category;
      session: SessionKey;
    }
  ) {
    return apiRequest<unknown>(`${PREFIX}/attendance/sessions/override`, {
      method: "DELETE",
      adminPassword,
      body: data,
    });
  },

  listHolidays(adminPassword: string, month: string) {
    return apiRequest<Holiday[]>(`${PREFIX}/holidays`, {
      adminPassword,
      params: { month },
    });
  },

  createHoliday(
    adminPassword: string,
    data: { date: string; name: string; applies_to: HolidayScope }
  ) {
    return apiRequest<Holiday>(`${PREFIX}/holidays`, {
      method: "POST",
      adminPassword,
      body: data,
    });
  },

  deleteHoliday(adminPassword: string, id: string) {
    return apiRequest<void>(`${PREFIX}/holidays/${id}`, {
      method: "DELETE",
      adminPassword,
    });
  },
};
